import { type ReactNode } from "react";

import { Carousel } from "../../../../components/ui/carousel";
import { useSupportCellHighlight } from "../../../../hooks/useSupportCellHighlight";
import { type AuditSession } from "../../../../models/audit/auditSession";
import { type LinkingData } from "../../../../pages/extraction/viewExtraction/PdfViewer";
import { DocumentViewerPanel } from "./attributeTestAnalysis/DocumentViewerPanel";
import { citationToBoundingBox } from "./attributeTestAnalysis/types";
import { WorkpaperFoldOut } from "./WorkpaperFoldOut";

interface SupportCellFoldOutProps {
  auditSession: AuditSession;
  procedureSessionId: string;
  sourceRowId?: string;
  supportColumnId?: string;
  /** The extracted support value (header context). */
  value?: string;
  onClose: () => void;
  /** The work-paper table, rendered as the left pane. */
  gridPane: ReactNode;
  /** Lifted grid scroll offset — see `WorkpaperFoldOut`. */
  gridScrollTopRef?: React.MutableRefObject<number>;
  /** Grid-pane open state and pane proportions, lifted to the parent so they
   * survive the per-cell remount — see `WorkpaperFoldOut`. */
  gridExpanded?: boolean;
  onGridExpandedChange?: (expanded: boolean) => void;
  paneLayout?: number[] | null;
  onPaneLayoutChange?: (layout: number[]) => void;
  /** Persist a drawn/repositioned citation box (drag + annotate). */
  onLinkingComplete: (linkingData: LinkingData) => Promise<void>;
  /** Delete a citation box. */
  onDelete: (params: { id?: string; index: number }) => Promise<void>;
  /** User id holding this support cell's edit lock, or null when unlocked. */
  lockedBy?: string | null;
  /** The current user's id, to allow editing a cell they themselves locked. */
  currentUserId?: string;
}

/**
 * Inline two-pane fold-out for a support cell: `grid | doc viewer`. The doc
 * viewer auto-opens on the cell's source citation; there is no middle pane.
 * Drag/delete/annotate editing is wired through to the same persistence
 * handlers as the legacy support side-panel, and is disabled when the session
 * is complete (handled by `DocumentViewerPanel`) or the cell is locked by
 * another user.
 */
export function SupportCellFoldOut({
  auditSession,
  procedureSessionId,
  sourceRowId,
  supportColumnId,
  value,
  onClose,
  gridPane,
  gridScrollTopRef,
  gridExpanded = true,
  onGridExpandedChange,
  paneLayout,
  onPaneLayoutChange,
  onLinkingComplete,
  onDelete,
  lockedBy,
  currentUserId,
}: SupportCellFoldOutProps) {
  const {
    citations,
    navigation,
    showDocumentViewer,
    viewerBoundingBoxes,
    selectedBoundingBoxId,
    selectedBoundingBoxIndex,
  } = useSupportCellHighlight({
    auditSession,
    procedureSessionId,
    sourceRowId,
    supportColumnId,
  });

  // Allow editing unless another user holds the cell's lock. The
  // session-complete gate is applied inside `DocumentViewerPanel`.
  const lockedByOther = lockedBy != null && lockedBy !== currentUserId;
  const allowEditing = !lockedByOther;

  // A support value can be read off several documents. The viewer shows one
  // document at a time, so number every citation and let the auditor step
  // through them; selecting one sets the navigation index, whose effect opens
  // that citation's document. The pager holds a fixed width — two arrows around
  // five chips, all 28px squares like the arrows (7×28 + 4×4 chip gaps + 2×4
  // track padding + 2×8 arrow gaps = 236px) — so it never moves as the document
  // name changes length; more citations scroll, and the carousel keeps the
  // selected chip in view.
  const chipClassName =
    "h-7 w-7 !p-0 !rounded-none inline-flex items-center justify-center";
  const citationPager =
    citations.length > 1 ? (
      <div className="w-[236px] shrink-0">
        <Carousel
          items={citations.map((citation, idx) => ({
            id: citation.id || idx,
            content: idx + 1,
          }))}
          selectedIndex={navigation.activeEvidenceIndex}
          onItemSelect={navigation.setActiveEvidenceIndex}
          itemClassName={chipClassName}
          // The selected chip's border would otherwise take the base light
          // grey and read as a pale ring around the black fill.
          selectedItemClassName={`${chipClassName} !border-stone-950`}
        />
      </div>
    ) : undefined;

  return (
    <WorkpaperFoldOut
      title={
        <span className="truncate text-font-secondary">
          {value ? `Support · ${value}` : "Support"}
        </span>
      }
      onClose={onClose}
      // Support cells open with the work-paper (grid) pane expanded by default
      // (an even grid | viewer split — unlike the attribute-test fold-out,
      // which starts the grid collapsed to a rail); after that the lifted state
      // and layout from the parent win, so they survive the per-cell remount.
      gridExpanded={gridExpanded}
      onGridExpandedChange={onGridExpandedChange}
      paneLayout={paneLayout}
      onPaneLayoutChange={onPaneLayoutChange}
      gridPane={gridPane}
      gridScrollTopRef={gridScrollTopRef}
      viewerPane={
        <DocumentViewerPanel
          showDocumentViewer={showDocumentViewer}
          currentBoundingBox={navigation.currentBoundingBox}
          viewerBoundingBoxes={viewerBoundingBoxes}
          selectedBoundingBoxId={selectedBoundingBoxId}
          selectedBoundingBoxIndex={selectedBoundingBoxIndex}
          currentSelectionValue={navigation.currentSelectionValue}
          allCitations={citations}
          allowEditing={allowEditing}
          allowKeyToggle={false}
          citationPager={citationPager}
          onLinkingComplete={onLinkingComplete}
          onDelete={onDelete}
          onToggleKeyCitation={async () => {}}
          onSelect={({ id }) => {
            if (!id) return;
            const clickedIndex = citations.findIndex((c) => c.id === id);
            if (clickedIndex < 0) return;
            const clicked = citations[clickedIndex];
            // Keep the pager on the box the auditor clicked.
            navigation.setActiveEvidenceIndex(clickedIndex);
            navigation.setCurrentBoundingBox(citationToBoundingBox(clicked));
            navigation.setCurrentSelectionValue(
              clicked.shortSummary || undefined,
            );
          }}
        />
      }
    />
  );
}
